import type { CSSProperties } from 'react';
import { HOME_INDICATOR_INSET, STATUS_BAR_INSET } from '@/shell/statusBarInset';
import { useKeyboardInset } from './useKeyboardInset';

/**
 * An overlay's two edges on a phone: the one rule every layer laid over the page reads (I-1163,
 * I-1209; `docs/concept-iphone-ux.md`, OV-1 to OV-4).
 *
 * The top below the status bar (`STATUS_BAR_INSET`), and the bottom above the home indicator, or the
 * tab bar where it stays (`HOME_INDICATOR_INSET`), except with the keyboard up, which nothing rests
 * under. `--modal-room` is the height left between them, for a dialog that keeps its own scroller and
 * caps it there rather than at the screen (`modalRoom.guard.test.ts`).
 *
 * `ModalFrame`'s layer reads it, and so does any overlay not built on the frame (a view's Customize
 * panel). An overlay that took its top from the status bar and nothing for its bottom put its Save
 * under the tab bar the app draws over the web view: 862 against 791 at 402 × 874 (OV-1).
 * `overlayEdges.guard.test.ts` fails on a `top: STATUS_BAR_INSET` read anywhere else. `Sheet`
 * measures its own: it covers the tab bar, so only the home indicator rests under it.
 */
export function useOverlayEdges(): CSSProperties {
  const keyboardUp = useKeyboardInset().covered > 0;
  const bottom = keyboardUp ? '0px' : HOME_INDICATOR_INSET;
  return {
    top: STATUS_BAR_INSET,
    bottom,
    '--modal-room': `calc(100dvh - ${STATUS_BAR_INSET} - ${bottom})`,
  } as CSSProperties;
}
